import {
  Badge,
  Box,
  Flex,
  Heading,
  Icon,
  IconButton,
  Link,
  Stack,
  Text,
  Tooltip,
  chakra,
} from "@chakra-ui/react";
import { Instant, LocalDate } from "@js-joda/core";
import React from "react";
import { FiExternalLink, FiMessageSquare, FiRefreshCw } from "react-icons/fi";
import { Messages } from "../../core/api";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { assertNever } from "../../utils";
import { deriveReviewStatus, type ReviewStatus } from "./reviewRules";
import { StatusPill } from "../care-and-task-management/tasks-v2/components/TasksV2StatusBadge";

export type DocumentQuestionComment = Messages["DocumentQuestionComment"];

export { deriveReviewStatus, type ReviewStatus } from "./reviewRules";

function statusPill(status: ReviewStatus): { colorScheme: string; label: string } {
  switch (status) {
    case "approved":
      return { colorScheme: "blue", label: "Approved" };
    case "submitted":
      return { colorScheme: "green", label: "Submitted" };
    case "reopened":
      return { colorScheme: "orange", label: "Reopened" };
    case "notSubmitted":
      return { colorScheme: "gray", label: "Not submitted" };
    default:
      return assertNever(status);
  }
}

function commentState(state: DocumentQuestionComment["state"]): {
  colorScheme: string;
  label: string;
} {
  switch (state) {
    case "open":
      return { colorScheme: "orange", label: "Open" };
    case "resolved":
      return { colorScheme: "green", label: "Fixed by RN" };
    case "declined":
      return { colorScheme: "purple", label: "RN replied" };
    case "stale":
      return { colorScheme: "gray", label: "Closed" };
    default:
      return assertNever(state);
  }
}

export interface ReviewSidePanelProps {
  approvedAt: LocalDate | null;
  /** Rendered above the sent comments (Task 7: unsent drafts). */
  beforeComments?: React.ReactNode;
  comments: DocumentQuestionComment[];
  /** Pinned under the scrolling content (Task 7: general comment + Reopen / Approve actions). */
  footer?: React.ReactNode;
  isOpenForResubmission: boolean;
  /** Spins the refresh button while the review is being refetched. */
  isRefreshing?: boolean;
  isSubmitted: boolean;
  /** Shown under the status (e.g. "Could not refresh this document"). */
  notice?: React.ReactNode;
  /** Next to "Open generated PDF" (e.g. "Regenerate PDF"). */
  pdfActions?: React.ReactNode;
  pdfUrl: string | null;
  /** Question id -> label shown on its comments. */
  questionLabels: Map<number, string>;
  submittedAt: Instant | null;
  /** Whether the question has a block in the preview the comment can scroll to. */
  canLocateQuestion: (questionId: number) => boolean;
  /** Per sent comment, e.g. a "Withdraw" button (Task 7). */
  renderCommentActions?: (comment: DocumentQuestionComment) => React.ReactNode;
  /** Reloads the review (e.g. to see changes made elsewhere meanwhile). */
  onRefresh?: () => void;
  onSelectComment: (comment: DocumentQuestionComment) => void;
}

export function SectionTitle(props: { children: React.ReactNode; count: number }) {
  return (
    <Flex align="center" gap={2} mb={2}>
      <Text
        color="gray.500"
        fontSize="11px"
        fontWeight="semibold"
        letterSpacing="wider"
        textTransform="uppercase"
      >
        {props.children}
      </Text>
      <Text color="gray.400" fontSize="xs">
        {props.count}
      </Text>
    </Flex>
  );
}

function CommentCard(props: {
  actions: React.ReactNode;
  canLocate: boolean;
  comment: DocumentQuestionComment;
  questionLabel: string | null;
  onSelect: () => void;
}) {
  const { comment } = props;
  const state = commentState(comment.state);
  const isOpen = comment.state === "open";

  const body = (
    <>
      <Flex align="center" gap={1.5} wrap="wrap">
        <StatusPill colorScheme={state.colorScheme} label={state.label} />
        {comment.source === "ai" && (
          <Tooltip hasArrow label="Raised by the automatic AI review" placement="top">
            <Badge borderRadius="sm" colorScheme="purple" fontSize="10px" variant="solid">
              AI
            </Badge>
          </Tooltip>
        )}
        <Text color="gray.400" fontSize="xs" ml="auto">
          {dateFormatter.toDateTime(comment.createdAt)}
        </Text>
      </Flex>
      {props.questionLabel !== null && (
        <Text color="gray.500" fontSize="xs" mt={1.5} noOfLines={1}>
          {props.questionLabel}
        </Text>
      )}
      <Text
        color={isOpen ? "gray.800" : "gray.600"}
        fontSize="sm"
        mt={1}
        overflowWrap="anywhere"
        whiteSpace="pre-wrap"
      >
        {comment.text}
      </Text>
      {comment.stateReason !== null &&
        comment.stateReason !== "" &&
        (comment.state === "declined" ? (
          // The RN's own words in answer to the comment ("Add narrative" on mobile).
          <Box
            bg="purple.50"
            borderColor="purple.300"
            borderLeftWidth={3}
            borderRadius="sm"
            mt={2}
            px={2.5}
            py={1.5}
          >
            <Text color="purple.700" fontSize="11px" fontWeight="semibold">
              RN&apos;s reply
            </Text>
            <Text color="gray.800" fontSize="sm" overflowWrap="anywhere" whiteSpace="pre-wrap">
              {comment.stateReason}
            </Text>
          </Box>
        ) : (
          <Text color="gray.500" fontSize="xs" fontStyle="italic" mt={1} overflowWrap="anywhere">
            {comment.stateReason}
          </Text>
        ))}
      <Flex align="center" gap={2} mt={1.5}>
        <Text color="gray.400" fontSize="xs">
          {comment.source === "ai" ? "AI review" : comment.createdByName ?? "Agency"}
          {!props.canLocate && " · question not shown in this view"}
        </Text>
      </Flex>
    </>
  );

  return (
    <Box
      bg="white"
      borderColor={isOpen ? "orange.200" : "gray.200"}
      borderLeftColor={isOpen ? "orange.400" : "gray.300"}
      borderLeftWidth={3}
      borderRadius="md"
      borderWidth={1}
    >
      {props.canLocate ? (
        <chakra.button
          _focusVisible={{ boxShadow: "outline", outline: "none" }}
          _hover={{ bg: "gray.50" }}
          borderRadius="md"
          display="block"
          px={3}
          py={2.5}
          textAlign="start"
          title="Show this question"
          type="button"
          w="full"
          onClick={props.onSelect}
        >
          {body}
        </chakra.button>
      ) : (
        <Box px={3} py={2.5}>
          {body}
        </Box>
      )}
      {props.actions !== null && props.actions !== undefined && (
        <Flex borderColor="gray.100" borderTopWidth={1} gap={1} justify="flex-end" px={2} py={1}>
          {props.actions}
        </Flex>
      )}
    </Box>
  );
}

function CommentGroup(props: {
  comments: DocumentQuestionComment[];
  panel: ReviewSidePanelProps;
  title: string;
}) {
  if (props.comments.length === 0) {
    return null;
  }
  return (
    <Box>
      <SectionTitle count={props.comments.length}>{props.title}</SectionTitle>
      <Stack spacing={2}>
        {props.comments.map((comment) => (
          <CommentCard
            key={comment.id}
            actions={props.panel.renderCommentActions?.(comment) ?? null}
            canLocate={props.panel.canLocateQuestion(comment.questionId)}
            comment={comment}
            questionLabel={props.panel.questionLabels.get(comment.questionId) ?? null}
            onSelect={() => props.panel.onSelectComment(comment)}
          />
        ))}
      </Stack>
    </Box>
  );
}

function hasContent(node: React.ReactNode): boolean {
  return node !== undefined && node !== null && node !== false;
}

function byNewestFirst(a: DocumentQuestionComment, b: DocumentQuestionComment): number {
  return b.createdAt.compareTo(a.createdAt);
}

export default function ReviewSidePanel(props: ReviewSidePanelProps) {
  const status = statusPill(
    deriveReviewStatus({
      approvedAt: props.approvedAt,
      isOpenForResubmission: props.isOpenForResubmission,
      isSubmitted: props.isSubmitted,
    })
  );
  const openComments = props.comments.filter((c) => c.state === "open").sort(byNewestFirst);
  const closedComments = props.comments.filter((c) => c.state !== "open").sort(byNewestFirst);

  return (
    <Flex bg="gray.50" direction="column" h="full" minH={0}>
      <Box flex={1} minH={0} overflowY="auto" px={5} py={5}>
        <Stack spacing={6}>
          <Box>
            <Flex align="center" gap={2} wrap="wrap">
              <StatusPill colorScheme={status.colorScheme} label={status.label} />
              {props.approvedAt !== null && (
                <Text color="gray.500" fontSize="xs">
                  on {dateFormatter.toDate(props.approvedAt)}
                </Text>
              )}
              {props.onRefresh !== undefined && (
                <Tooltip hasArrow label="Refresh" placement="top">
                  <IconButton
                    aria-label="Refresh"
                    color="gray.500"
                    icon={<Icon as={FiRefreshCw} boxSize={3.5} />}
                    isLoading={props.isRefreshing}
                    ml="auto"
                    size="xs"
                    variant="ghost"
                    onClick={props.onRefresh}
                  />
                </Tooltip>
              )}
            </Flex>
            <Text color="gray.600" fontSize="sm" mt={2}>
              {props.submittedAt !== null
                ? `Submitted ${dateFormatter.toDateTime(props.submittedAt)}`
                : "Not submitted yet"}
            </Text>
            {(props.pdfUrl !== null || hasContent(props.pdfActions)) && (
              <Flex align="center" gap={3} mt={2} wrap="wrap">
                {props.pdfUrl !== null && (
                  <Link
                    isExternal
                    alignItems="center"
                    color="blue.600"
                    display="inline-flex"
                    fontSize="sm"
                    fontWeight="medium"
                    gap={1}
                    href={props.pdfUrl}
                  >
                    Open generated PDF
                    <Icon as={FiExternalLink} boxSize={3.5} />
                  </Link>
                )}
                {props.pdfActions}
              </Flex>
            )}
            {props.notice}
          </Box>

          <Box>
            <Heading color="gray.800" fontWeight="semibold" mb={3} size="sm">
              Comments
            </Heading>
            <Stack spacing={5}>
              {props.beforeComments}
              {props.comments.length === 0 ? (
                hasContent(props.beforeComments) ? null : (
                  <Flex
                    align="center"
                    bg="white"
                    borderColor="gray.200"
                    borderRadius="md"
                    borderStyle="dashed"
                    borderWidth={1}
                    direction="column"
                    gap={1.5}
                    py={6}
                  >
                    <Icon as={FiMessageSquare} boxSize={5} color="gray.300" />
                    <Text color="gray.500" fontSize="sm">
                      No comments on this document.
                    </Text>
                  </Flex>
                )
              ) : (
                <>
                  <CommentGroup comments={openComments} panel={props} title="Open" />
                  <CommentGroup comments={closedComments} panel={props} title="Closed" />
                </>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
      {props.footer !== undefined && props.footer !== null && (
        <Box bg="white" borderColor="gray.200" borderTopWidth={1} px={5} py={4}>
          {props.footer}
        </Box>
      )}
    </Flex>
  );
}
